/**
 * 图片压缩工具
 *
 * 用于报名端图片上传：超过目标大小的图片通过 canvas 按最长边等比缩放，
 * 并按质量梯度逐级压缩为 jpeg，返回第一个满足目标大小的文件；
 * 全部梯度仍超限时返回压缩得最小的文件。
 * 已满足目标大小、非图片类型（含GIF）或压缩过程异常的文件原样返回，保证上传流程不中断。
 *
 * 使用方式：
 *   import { compressImage } from '@/util/image';
 *   // el-upload 的 before-upload 中返回 Promise，压缩后的文件会替换原文件上传
 *   beforeImageUpload(file) {
 *     ...
 *     return compressImage(file);
 *   }
 */

// 单文件压缩目标大小（200KB）
const TARGET_SIZE = 200 * 1024;
// 压缩尝试的最长边梯度（像素）
const EDGE_STEPS = [1600, 1280, 1024];
// 每个尺寸下的质量梯度
const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5];

/**
 * 压缩图片至目标大小以内
 * @param file 原始文件（File）
 * @param maxSize 目标大小（字节），默认200KB
 * @returns Promise<File> 压缩后的jpeg文件（无需压缩或压缩失败时为原文件）
 */
export function compressImage(file, maxSize = TARGET_SIZE) {
  return new Promise((resolve) => {
    // 已在目标大小以内、非图片或GIF（canvas重编码会破坏动图）时原样返回
    if (!file || file.size <= maxSize || !/^image\//.test(file.type) || file.type === 'image/gif') {
      resolve(file);
      return;
    }
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        resolve(compressBySteps(image, file, maxSize));
      } catch (e) {
        resolve(file);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    image.src = objectUrl;
  });
}

/**
 * 按最长边与质量梯度逐级压缩，返回第一个满足目标大小的jpeg文件；全部超限时返回压缩得最小的文件
 * @param image 已加载的图片对象
 * @param file 原始文件（用于命名）
 * @param maxSize 目标大小（字节）
 * @returns File 压缩后的jpeg文件
 */
function compressBySteps(image, file, maxSize) {
  let smallest = null;
  for (const maxEdge of EDGE_STEPS) {
    for (const quality of QUALITY_STEPS) {
      const compressed = drawToJpg(image, maxEdge, quality, file.name);
      if (compressed.size <= maxSize) {
        return compressed;
      }
      if (!smallest || compressed.size < smallest.size) {
        smallest = compressed;
      }
    }
  }
  return smallest || file;
}

/**
 * 按最长边等比缩放绘制白底画布并导出为jpeg文件
 */
function drawToJpg(image, maxEdge, quality, name) {
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext('2d');
  // 白底填充，避免透明PNG转JPEG后背景发黑
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const blob = dataUrlToBlob(dataUrl);
  return new File([blob], toJpgName(name), { type: 'image/jpeg' });
}

function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const binary = atob(parts[1]);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}

/**
 * 存储文件名统一转为jpg扩展名（压缩后为jpeg编码）
 */
function toJpgName(name) {
  const base = name ? name.replace(/\.[^.]*$/, '') : 'image';
  return base + '.jpg';
}
